import { useContext, useEffect, useId, useMemo, useState } from "react";
import app from "nystem";
import { ContentTypeView, DatabaseSearchContext } from "nystem-components";

const pupilTable = (items) => {
  for (const item of items) {
    if (item.type === "multigroup" && item.id === "pupils" && item.format === "table") return item;
    if (item.item) {
      const table = pupilTable(item.item);
      if (table) return table;
    }
  }
};

const ViewListComparison = ({ view }) => {
  const { search, loading } = useContext(DatabaseSearchContext);
  const comparisonId = useId();
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const comparisonView = useMemo(
    () => ({
      format: "view",
      item: [
        {
          type: "style",
          className: ["overflow-x-auto", "rounded-lg", "border", "border-gray-200", "bg-white"],
          item: [pupilTable(app.populatedViews.result.views.view.item)],
        },
      ],
    }),
    [],
  );

  const documentFields = useMemo(
    () =>
      app.contentType.result.item
        .find(({ id }) => id === "document")
        .item.map((field) => ({
          ...field,
          view: {
            format: "view",
            item: [{ type: "group", id: "document", item: [{ ...field, fallback: "—", className: ["break-words"] }] }],
          },
        })),
    [],
  );

  useEffect(() => {
    let mounted = true;
    setError("");
    setResults(null);
    if (search.searchTotal === undefined) return;
    (async () => {
      if (search.error) throw new Error(search.error);
      const results = [...(search.data || [])];
      let position = results.length;
      while (position < search.searchTotal) {
        const query = await view.event("search", { ...search, data: undefined, position });
        if (!mounted) return;
        if (query.error || !query.data?.length)
          throw new Error(query.error || "Could not load all extraction results.");
        results.push(...query.data);
        position += query.data.length;
      }
      if (mounted) setResults(results.filter(({ status }) => status === "complete"));
    })().catch((error) => {
      if (mounted) setError(error.message);
    });
    return () => {
      mounted = false;
    };
  }, [search, view]);

  if (error)
    return (
      <p role="alert" className="text-red-700">
        {error}
      </p>
    );
  if (loading || !results) return <p role="status">Loading extraction results…</p>;
  if (!results.length) return <p>No completed extractions for this upload yet.</p>;

  const pupils = new Map();
  for (const result of results) {
    const occurrences = new Map();
    for (const [index, pupil] of (result.pupils || []).entries()) {
      const nameImageIndex = pupil.nameImageIndex ?? pupil.sourceImageIndexes?.[0];
      const rowNumber = /^\d+$/.test(String(pupil.position ?? "")) ? Number(pupil.position) : pupil.rowNumber;
      const identity =
        rowNumber !== undefined && rowNumber !== null
          ? `${nameImageIndex ?? "unknown"}:${pupil.section ?? ""}:${rowNumber}`
          : `unmatched:${result._id}:${index}`;
      const occurrence = occurrences.get(identity) || 0;
      occurrences.set(identity, occurrence + 1);
      const key = `${identity}:${occurrence}`;
      if (!pupils.has(key)) pupils.set(key, { nameImageIndex, rowNumber, pupils: new Map() });
      pupils.get(key).pupils.set(result._id, pupil);
    }
  }

  const colors = results.map((_, index) => `hsl(${210 + (index * 360) / results.length} 65% 94%)`);

  return (
    <div className="space-y-4">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="mb-2 text-left text-lg font-semibold">Models used</caption>
        <thead>
          <tr>
            <th className="border-b p-2">Model / provider</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result, index) => (
            <tr key={result._id} style={{ backgroundColor: colors[index] }}>
              <td className="border-b p-2">
                <ContentTypeView contentType="result" format="comparisonModel" value={result} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-4 text-xl font-semibold">Document information</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {documentFields.map((field) => (
            <div key={field.id} className={`min-w-0 ${field.type === "textarea" ? "sm:col-span-2" : ""}`}>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{field.text}</h4>
              {results.map((result, index) => (
                <div key={result._id} className="min-h-6 px-2 py-1" style={{ backgroundColor: colors[index] }}>
                  <ContentTypeView contentType="result" view={field.view} value={result} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
      <div id={comparisonId}>
        <style>
          {results
            .map(
              (_, index) => `
          [id="${comparisonId}"] tbody > tr:nth-child(${results.length}n + ${index + 1}) {
            background-color: ${colors[index]};
          }
        `,
            )
            .join("\n") +
            `
              [id="${comparisonId}"] tbody > tr > td:first-child { border-left: 2px solid #9ca3af; }
              [id="${comparisonId}"] tbody > tr > td:last-child { border-right: 2px solid #9ca3af; }
              [id="${comparisonId}"] tbody > tr:nth-child(${results.length}n + 1) > td { border-top: 2px solid #9ca3af; }
              [id="${comparisonId}"] tbody > tr:nth-child(${results.length}n) > td { border-bottom: 2px solid #9ca3af; }
              [id="${comparisonId}"] tbody > tr:not(:nth-child(${results.length}n + 1)) > td:first-child > * { visibility: hidden; }
            `}
        </style>
        <ContentTypeView
          contentType="result"
          view={comparisonView}
          value={{
            pupils: [...pupils]
              .sort(
                ([, a], [, b]) =>
                  (a.nameImageIndex ?? Infinity) - (b.nameImageIndex ?? Infinity) ||
                  (a.rowNumber ?? Infinity) - (b.rowNumber ?? Infinity),
              )
              .flatMap(([, row]) =>
                results.map((result) => row.pupils.get(result._id) || { rowNumber: row.rowNumber }),
              ),
          }}
        />
      </div>
    </div>
  );
};

export default ViewListComparison;
